import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AddOption = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [optionColor, setOptionColor] = useState("#ffffff");
  const [surcharge, setSurcharge] = useState("");

  const colorOptions = [
    { label: "Red", value: "#ff0000" },
    { label: "Green", value: "#00ff00" },
    { label: "Blue", value: "#0000ff" },
    { label: "Yellow", value: "#ffff00" },
    { label: "Purple", value: "#800080" },
    { label: "Orange", value: "#ffa500" },
    { label: "Black", value: "#000000" },
  ];

  const handleColorChange = (value: string) => {
    if (value === "") {
      setOptionColor("#ffffff");
    } else {
      const selectedColor = colorOptions.find(
        (option) => option.value === value,
      );
      setOptionColor(selectedColor ? selectedColor.value : "");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || !axiosInstance) return;
    const optionData = {
      branch: user?.selectedBranch?.id,
      name,
      description,
      color: optionColor,
      surcharge: surcharge || "0",
    };
    try {
      const apiUrl = import.meta.env.VITE_APP_REDBIRDPOSBE_OPTION_INFORMATION;
      await axiosInstance.post(apiUrl, optionData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      toast.success(t("back.management.menu.option.successadd"));
      navigate("/menu/options");
    } catch (error: any) {
      console.error("Error adding option:", error);
      // Extract error message from response
      let errorMessage = t("back.management.menu.option.failadd");
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = Object.values(error.response.data).flat().join(", ");
        }
      }
      toast.error(errorMessage);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.option.addNewOption")}
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.option.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-full">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.menu.option.optionName")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t("back.management.menu.option.enterOptionName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
              <div className="w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="color"
                >
                  {t("back.management.menu.option.optionColor")}
                </label>
                <select
                  id="color"
                  value={optionColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                >
                  <option value="">
                    {t("back.management.menu.option.selectColor")}
                  </option>
                  {colorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {optionColor && (
                  <FontAwesomeIcon
                    icon={faCircle}
                    color={optionColor}
                    className="mt-2"
                  />
                )}
              </div>
            </div>
            <div className="mt-2">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.option.description")}
              </label>
              <textarea
                id="description"
                placeholder={t("back.management.menu.option.enterDescription")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="block h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.option.surcharge")}
            </h2>
            <input
              type="text"
              placeholder={t("back.management.menu.option.enterSurcharge")}
              value={surcharge}
              onChange={(e) => setSurcharge(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/options"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.option.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.option.confirmbutton")}
            </button>
          </div>
        </form>
      </div>
    </PageLoader>
  );
};

export default AddOption;
